import React, { Suspense, useEffect, useMemo, useState } from "react";
import {
  Switch,
  Route,
  Redirect,
  useHistory,
  useLocation,
} from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import SignInPage from "./SignInPage";
import ProjectsPage from "./homepage/ProjectsPage";
import Header from "./header/Header";
import NewProjectPage from "./homepage/NewProjectPage";
import Spinner from "./components/Spinner";
import { ProjectAccessGate } from "./auth/ProjectAccessGate";
import GlobalErrorHandler, {
  GlobalErrorHandlerContext,
} from "./components/GlobalErrorHandler";
import { HeadProvider, Meta } from "react-head";
import { useAuth0 } from "@auth0/auth0-react";
import * as Sentry from "@sentry/react";
import { CameraOptions } from "mapbox-gl";
import { ClientCacheManagerProvider } from "./offline/ClientCacheManager";
import OfflineToastNotification from "./offline/OfflineToastNotification";
import OfflineResponsesToastNotification from "./offline/OfflineResponsesToastNotification";
import DeveloperApiPage from "./DeveloperAPIPage";
import { Helmet } from "react-helmet";
import LandingPage from "./homepage/LandingPage";
import TeamPage from "./homepage/TeamPage";
import SiteHelmet from "./homepage/SiteHelmet";
import {
  CaseStudiesIndex,
  AzoresCaseStudy,
  BelizeCaseStudy,
  BrazilCaseStudy,
  CaliforniaCaseStudy,
  FSMCaseStudy,
  KiribatiCaseStudy,
  MaldivesCaseStudy,
  NavukavuCaseStudy,
} from "./homepage/caseStudies/caseStudies";
import Footer from "./footer/Footer";

const LazyProjectApp = React.lazy(
  () => import(/* webpackChunkName: "ProjectApp" */ "./projects/ProjectApp")
);
const LazyProjectAdmin = React.lazy(
  () => import(/* webpackChunkName: "AdminApp" */ "./admin/AdminApp")
);
const LazyAuthLanding = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ProjectInviteLanding" */ "./auth/ProjectInviteLanding"
    )
);
const LazySurveyApp = React.lazy(
  () => import(/* webpackChunkName: "SurveyApp" */ "./surveys/SurveyApp")
);
const LazyBasemapEditor = React.lazy(
  () =>
    import(
      /* webpackChunkName: "AdminEditBasemapPage" */ "./admin/data/EditBasemapPage"
    )
);
const LazySurveyFormEditor = React.lazy(
  () =>
    import(
      /* webpackChunkName: "AdminSurveyFormEditor" */ "./admin/surveys/SurveyFormEditor"
    )
);
const LazySubmitOfflineResponsesPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "OfflineSurveys" */ "./offline/SubmitOfflineResponsesPage"
    )
);
const LazyMapPortalHostingPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "MapPortalHosting" */ "./homepage/useCases/MapPortalHosting"
    )
);
const LazyOceanUseSurveysPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "OceanUseSurveys" */ "./homepage/useCases/OceanUseSurveys"
    )
);
const LazySketchingAndAnalysisPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "SketchingAndAnalysis" */ "./homepage/useCases/SketchingAndAnalysis"
    )
);

const LazyFullScreenOfflinePage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "OfflineSurveys" */ "./offline/FullScreenOfflineNavigation"
    )
);

const LazyAccountSettingsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "AccountSettingsPage" */ "./auth/CacheSettingsPage"
    )
);

const LazyJoinProject = React.lazy(
  () => import(/* webpackChunkName: "JoinProject" */ "./auth/JoinProject")
);

const LazyTermsOfUse = React.lazy(
  () => import(/* webpackChunkName: "TermsOfUse" */ "./TermsOfUse")
);

const LazyPrivacyPolicy = React.lazy(
  () => import(/* webpackChunkName: "PrivacyPolicy" */ "./PrivacyPolicy")
);

const LazySuperuserDashboard = React.lazy(
  () => import(/* webpackChunkName: "SuperuserDashboard" */ "./Dashboard")
);

/** Public marketing pages that should start at the top on forward navigation. */
function shouldScrollMarketingPageToTop(pathname: string, hash: string) {
  if (hash) {
    return false;
  }
  if (pathname.startsWith("/uses/") || pathname.startsWith("/case-studies")) {
    return true;
  }
  return [
    "/",
    "/projects",
    "/api",
    "/team",
    "/new-project",
    "/terms-of-use",
    "/privacy-policy",
    "/signin",
    "/submit-offline-surveys",
    "/account-settings",
  ].includes(pathname);
}

function App() {
  const { user } = useAuth0();
  useTranslation("homepage");
  const [error, setError] = useState<Error | null>(null);
  const history = useHistory();
  const location = useLocation();
  const isUseCaseRoute = location.pathname.startsWith("/uses/");
  const isDarkRoutes =
    isUseCaseRoute ||
    location.pathname === "/" ||
    location.pathname === "/about" ||
    location.pathname === "/projects" ||
    location.pathname === "/features" ||
    location.pathname === "/partners" ||
    location.pathname === "/api" ||
    location.pathname === "/team" ||
    location.pathname === "/signin" ||
    location.pathname === "/authenticate" ||
    location.pathname === "/account-settings" ||
    location.pathname === "/submit-offline-surveys" ||
    location.pathname === "/terms-of-use" ||
    location.pathname === "/privacy-policy" ||
    location.pathname === "/new-project";
  useEffect(() => {
    if (user) {
      Sentry.setUser({ email: user.email, id: user.sub });
    } else {
      Sentry.configureScope((scope) => scope.setUser(null));
    }
  }, [user]);

  // Smooth-scroll to in-page anchors like /#funders
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        // Wait for layout
        requestAnimationFrame(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      }
    }
  }, [location.pathname, location.hash]);

  // Scroll marketing pages to top on forward navigation only. Using pathname
  // in a mount effect also ran on HMR remounts and POP (back/forward), which
  // fought browser scroll restoration during development and when using the
  // back button. Hash navigations (e.g. /#use-cases) are handled separately.
  useEffect(() => {
    return history.listen((nextLocation, action) => {
      if (
        (action === "PUSH" || action === "REPLACE") &&
        shouldScrollMarketingPageToTop(
          nextLocation.pathname,
          nextLocation.hash
        )
      ) {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      }
    });
  }, [history]);

  const frontOfTheHouse = [
    "/signin",
    "/projects",
    "/new-project",
    "/authenticate",
    "/",
    "/api",
    "/team",
    "/terms-of-use",
    "/privacy-policy",
    "/uses/map-portal-hosting",
    "/uses/ocean-use-surveys",
    "/uses/sketching-and-analysis",
    "/case-studies",
    "/case-studies/*",
  ];
  return (
    <div
      className={
        isDarkRoutes ? "min-h-screen bg-slate-950 text-slate-100" : "App"
      }
    >
      <HeadProvider>
        <Meta name="theme-color" content="#267588" />
        <Suspense
          fallback={
            <div
              style={{ height: "100vh" }}
              className="w-full flex min-h-full h-96 justify-center text-center align-middle items-center content-center justify-items-center place-items-center place-content-center"
            >
              <Spinner />
            </div>
          }
        >
          <GlobalErrorHandlerContext.Provider value={{ error, setError }}>
            <ClientCacheManagerProvider>
              <AuthoritativeDomainPrompt />
              <Route path={frontOfTheHouse} exact>
                <Helmet>
                  <html className="bg-black" />
                </Helmet>
              </Route>
              <Route
                path={[...frontOfTheHouse, "/submit-offline-surveys"]}
                exact
              >
                <Header />
              </Route>
              <Route
                path={["/projects", "/new-project", "/api", "/team"]}
                exact
              >
                <OfflineToastNotification />
                <OfflineResponsesToastNotification />
              </Route>
              <Switch>
                <Route path="/auth/projectInvite">
                  <LazyAuthLanding />
                </Route>
                <Route path="/signin">
                  <SignInPage />
                </Route>
                <Route exact path="/projects">
                  <ProjectsPage />
                </Route>
                <Route exact path="/case-studies">
                  <CaseStudiesIndex />
                </Route>
                <Route exact path="/case-studies/azores">
                  <AzoresCaseStudy />
                </Route>
                <Route exact path="/case-studies/belize">
                  <BelizeCaseStudy />
                </Route>
                <Route exact path="/case-studies/brazil">
                  <BrazilCaseStudy />
                </Route>
                <Route exact path="/case-studies/california">
                  <CaliforniaCaseStudy />
                </Route>
                <Route exact path="/case-studies/fsm">
                  <FSMCaseStudy />
                </Route>
                <Route exact path="/case-studies/kiribati">
                  <KiribatiCaseStudy />
                </Route>
                <Route exact path="/case-studies/maldives">
                  <MaldivesCaseStudy />
                </Route>
                <Route exact path="/case-studies/navukavu">
                  <NavukavuCaseStudy />
                </Route>
                <Route exact path="/api">
                  <DeveloperApiPage />
                </Route>
                <Route exact path="/team">
                  <TeamPage />
                </Route>
                <Route exact path="/uses/map-portal-hosting">
                  <LazyMapPortalHostingPage />
                </Route>
                <Route exact path="/uses/ocean-use-surveys">
                  <LazyOceanUseSurveysPage />
                </Route>
                <Route exact path="/uses/sketching-and-analysis">
                  <LazySketchingAndAnalysisPage />
                </Route>
                <Route exact path="/uses/map-portal">
                  <Redirect to="/uses/map-portal-hosting" />
                </Route>
                <Route exact path="/uses/surveys">
                  <Redirect to="/uses/ocean-use-surveys" />
                </Route>
                <Route exact path="/uses/planning">
                  <Redirect to="/uses/sketching-and-analysis" />
                </Route>
                <Route exact path="/terms-of-use">
                  <LazyTermsOfUse />
                </Route>
                <Route exact path="/privacy-policy">
                  <LazyPrivacyPolicy />
                </Route>
                <Route path="/new-project">
                  <NewProjectPage />
                </Route>
                <Route exact path="/dashboard">
                  <LazySuperuserDashboard />
                </Route>
                <Route path="/authenticate">
                  <span>
                    <Trans ns="homepage">authenticating...</Trans>
                  </span>
                </Route>
                <Route path="/account-settings">
                  <LazyAccountSettingsPage />
                </Route>
                <Route path="/submit-offline-surveys">
                  <LazySubmitOfflineResponsesPage />
                </Route>
                <Route exact path="/">
                  <OfflineResponsesToastNotification />
                  <LazyFullScreenOfflinePage />
                  <LandingPage />
                  <SiteHelmet
                    title="SeaSketch"
                    description="SeaSketch is the platform for marine spatial planning. Publish maps, survey stakeholders, and design better ocean plans."
                    path="/"
                  />
                  {/* <h1 className="mx-auto max-w-xl mt-2 mb-8 text-3xl text-left sm:text-center leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl sm:leading-10">
                      {t(
                        "SeaSketch Supports Collaborative Planning for our Oceans"
                      )}
                    </h1>
                    <p className="max-w-4xl mx-auto">
                      {t(`SeaSketch puts powerful tools into the hands of ocean planners,
              stakeholders and the public that were once limited to GIS
              professionals, enabling participatory marine spatial planning
              processes that are closely tied to the relevant science and
              information. SeaSketch is being used around the globe in small
              agency teams and large community-driven initiatives to make better
              management decisions every day.`)}
                    </p> */}
                  {/* <NewProjectCTA /> */}
                </Route>
                <Route
                  exact
                  path="/:slug"
                  render={(params) => (
                    <Redirect to={`/${params.match.params.slug}/app`} />
                  )}
                />
                <Route exact path="/:slug/join">
                  {/* <ProjectAccessGate> */}
                  <LazyJoinProject />
                  {/* </ProjectAccessGate> */}
                </Route>
                <Route path="/:slug">
                  <Route
                    exact
                    path={`/:slug/edit-basemap/:id`}
                    render={(history) => {
                      const { id } = history.match.params;
                      const search = new URLSearchParams(
                        history.location.search || ""
                      );
                      const encodedCameraOptions = search.get("camera");
                      let cameraOptions: CameraOptions | undefined = undefined;
                      if (encodedCameraOptions) {
                        try {
                          const decoded = atob(encodedCameraOptions);
                          cameraOptions = JSON.parse(decoded);
                        } catch (e) {
                          console.warn(
                            "Problem decoding cameraOptions search parameter"
                          );
                          console.warn(e);
                        }
                      }
                      return (
                        <ProjectAccessGate admin={true}>
                          <LazyBasemapEditor
                            id={parseInt(id)}
                            returnToUrl={search.get("returnToUrl")}
                            cameraOptions={cameraOptions}
                          />
                        </ProjectAccessGate>
                      );
                    }}
                  ></Route>

                  <Route
                    path="/:slug/survey-editor/:surveyId/:subpath?"
                    render={(history) => {
                      const { slug, surveyId, subpath } = history.match.params;
                      let formElementId: number | null = null;
                      let route: "formElement" | "basic" | "logic" =
                        "formElement";
                      if (subpath) {
                        if (subpath === "basic") {
                          route = "basic";
                        } else if (subpath === "logic") {
                          route = "logic";
                        } else {
                          formElementId = parseInt(subpath);
                        }
                      }
                      return (
                        <ProjectAccessGate admin={true}>
                          <LazySurveyFormEditor
                            slug={slug}
                            surveyId={parseInt(surveyId)}
                            formElementId={formElementId}
                            route={route}
                          />
                        </ProjectAccessGate>
                      );
                    }}
                  ></Route>
                  <Route path="/:slug/admin">
                    <LazyProjectAdmin />
                  </Route>
                  <Route path={["/:slug/profile", "/:slug/app/:sidebar?"]}>
                    <ProjectAccessGate>
                      <LazyProjectApp />
                    </ProjectAccessGate>
                  </Route>
                  <Route
                    path="/:slug/surveys/:surveyId"
                    exact
                    render={(history) => {
                      const { slug, surveyId } = history.match.params;
                      return <Redirect to={`/${slug}/surveys/${surveyId}/0`} />;
                    }}
                  />
                  <Route path="/:slug/surveys/:surveyId/:position/:practice?">
                    <LazySurveyApp />
                  </Route>
                </Route>
              </Switch>
              <GlobalErrorHandler />
              <Route path={frontOfTheHouse} exact>
                <Footer />
              </Route>
            </ClientCacheManagerProvider>
          </GlobalErrorHandlerContext.Provider>
        </Suspense>
      </HeadProvider>
    </div>
  );
}

export default App;

/**
 * If window.location.hostname is "next.seasket.ch", show a prompt for the
 * user to access seasketch from www.seasketch.org by showing a link. The
 * user may dismiss the prompt, and if so the dismissal will be remembered in localstorage
 */
function AuthoritativeDomainPrompt() {
  const { t } = useTranslation("homepage");
  const [dismissed, setDismissed] = useState(
    localStorage.getItem("authoritativeDomainPromptDismissed") === "true"
  );
  const location = useLocation();

  const wwwLink = useMemo(() => {
    const url = new URL(window.location.toString());
    url.hostname = "www.seasketch.org";
    return url.toString();
  }, [window.location.toString()]);

  const top = useMemo(() => {
    switch (location.pathname) {
      case "/":
      case "/about":
      case "/api":
      case "/projects":
        return false;
      default:
        return true;
    }
  }, [location.pathname]);

  if (window.location.hostname === "next.seasket.ch" && !dismissed) {
    return (
      <div
        className={`z-50 fixed left-0 right-0 bg-yellow-100 p-4 text-gray-800  ${
          top
            ? "top-0 shadow border-b border-gray-800"
            : "bottom-0 border-t border-gray-300"
        }`}
      >
        <div className="flex flex-row items-center">
          <span className="flex-grow">
            <Trans ns="homepage">
              You are accessing SeaSketch from the beta domain next.seasket.ch.
              For the best experience, please use{" "}
              <a className="text-primary-500 underline" href={wwwLink}>
                www.seasketch.org
              </a>
              . If you are using offline features{" "}
              <a
                className="text-primary-500 underline"
                href="https://github.com/seasketch/next/wiki/Offline-instructions-for-domain-change"
              >
                read these instructions
              </a>
              .
            </Trans>
          </span>
          <button
            className="ml-4"
            onClick={() => {
              setDismissed(true);
              localStorage.setItem(
                "authoritativeDomainPromptDismissed",
                "true"
              );
            }}
          >
            {t("Dismiss")}
          </button>
        </div>
      </div>
    );
  }
  return null;
}
